import type { ActiveHit, AttackDef, Boss, Player } from './types'
import { arenaBoundsFor, PLAYER, PLAYER_DISPLAY_HEIGHT } from './balance'
import { spawnTimedSpirit } from './awoken_rampage_v5'
import { HUNT_TAKEOFF_KEYS, HUNT_DURATION, HUNT_FLOOR, HUNT_WALLS,
  huntStudyArt, huntRealPlacement, huntRealArtBounds, huntRealArtPoint,
  sampleWallHuntSteppedSized } from './awoken_wall_hunt_study'
import type { WallSide } from './awoken_wall_hunt_study'
import { wallDashFix, wallDashFixAttack, wallDashLateMs, wallDashPounceEase, WALL_DASH_POUNCE_MAX_PX, WALL_DASH_POUNCE_REACH_PX } from './awoken_wall_dash_fix'

// Adopted runtime. DEV entry points explicitly select the new default or the old comparison.
// Keep the module-only DEV default off for isolated legacy attack tests; main and the lab initialize it.
let enabled = false
export function setWallHuntGame(value: boolean): void {
  enabled = import.meta.env.DEV && value
  if (enabled) preloadWallHuntArt()
}
export function wallHuntGameEnabled(): boolean { return !import.meta.env.DEV || enabled }
const keyTime = (image: string) => HUNT_TAKEOFF_KEYS.find(k => k.image === image)!.ms
export const WALL_HUNT_TIMES = {
  launch: keyTime('strike'), wall: keyTime('wall'), kick:  HUNT_TAKEOFF_KEYS.find(k => k.ms > keyTime('wall'))!.ms,
  land: HUNT_TAKEOFF_KEYS.find(k => k.image === 'catch')!.ms, end: HUNT_DURATION,
} as const
const T = WALL_HUNT_TIMES
export const isWallHuntAttack = (id: string | undefined): boolean => id === 'feralWallDash' || id === 'feralWallDashBack'
export function wallHuntGameAttack(atk: AttackDef): AttackDef {
  if (!wallHuntGameEnabled() || !isWallHuntAttack(atk.id)) return atk
  const back = atk.id === 'feralWallDashBack'
  return { ...wallDashFixAttack(atk), wallHuntV2: true, activeMs: back ? T.land - T.kick : T.wall - T.launch,
    recoveryMs: back ? Math.max(atk.recoveryMs, T.end - T.land) : atk.recoveryMs,
    lungeDistance: undefined, wallContactInsetPx: undefined, turnsDuringTelegraph: false }
}
export function isWallHuntGame(boss: Boss): boolean {
  return wallHuntGameEnabled() && boss.id === 'crowned-king-awoken' && boss.hp > 0
    && isWallHuntAttack(boss.currentAttack?.id)
    && ['telegraph', 'active', 'beatGap', 'recovery'].includes(boss.state)
}
export function wallHuntLabTime(boss: Boss): number {
  const atk = boss.currentAttack!
  const ms = Math.max(0, boss.stateElapsed * 1000)
  if (atk.id === 'feralWallDash') {
    if (boss.state === 'telegraph') return Math.min(T.launch - 0.001, ms / atk.chargeMs * T.launch)
    if (boss.state === 'active') return Math.min(T.wall, T.launch + wallDashLateMs(ms, T.wall - T.launch))
    return T.wall
  }
  if (boss.state === 'telegraph') return T.wall + Math.min(1 - 1e-9, ms / atk.chargeMs) * (T.kick - T.wall)
  if (boss.state === 'active') return Math.min(T.land, T.kick + ms)
  return Math.min(T.end, T.land + ms)
}
function rawSample(ms: number, side: WallSide) {
  const frame = sampleWallHuntSteppedSized(ms, side, 'six', 'high', 'weak', 'real', 'A')
  const art = huntStudyArt(frame, 'old', 'own', { wall: 'B' }, 'head8')!
  return { frame, art, placement: huntRealPlacement(frame, side, art, 'fix') }
}
function pixelCellBounds(sample: ReturnType<typeof rawSample>): [number, number, number, number] {
  const { frame, art, placement: p } = sample
  const b = huntRealArtBounds(frame, p, art)
  // The lab's hull contains pixel origins. Rendering/collision use full 1x1 cells.
  // Their exact support is the hull plus the two transformed pixel basis vectors.
  const ex = Math.cos(p.rotation) * p.scaleX * frame.facing, ey = Math.sin(p.rotation) * p.scaleX
  const fx = -Math.sin(p.rotation) * p.scaleY * frame.facing, fy = Math.cos(p.rotation) * p.scaleY
  return [b[0] + Math.min(0, ex) + Math.min(0, fx), b[1] + Math.min(0, ey) + Math.min(0, fy),
    b[2] + Math.max(0, ex) + Math.max(0, fx), b[3] + Math.max(0, ey) + Math.max(0, fy)]
}
// ---- Return-leap aim (DEV comparison: fixed = the v2 path untouched / aim / half) ----
// The player x is recorded once, at the kick (telegraph -> active). The return path's
// horizontal travel measured from the kick-off point is stretched by k (k = 1 is the
// fixed path); height, timing and drawing order never change. The post-landing slide
// is added in its original px. See docs/handoffs/2026-09-24-awoken-wall-hunt-study.md.
export type WallHuntAim = 'fixed' | 'aim' | 'half'
let aimMode: WallHuntAim = 'fixed'
export function setWallHuntAim(value: string | null | undefined): void {
  aimMode = import.meta.env.DEV && (value === 'aim' || value === 'half') ? value : 'fixed'
}
export function wallHuntAimMode(): WallHuntAim { return import.meta.env.DEV ? aimMode : 'fixed' }
/** Measured centre of the fixed path's standing-hit band (369..834px from the clinging wall, 840px room). */
export const WALL_HUNT_AIM_CENTER_PX = 601.5
/** Extra lean added to the swoop drawing when the path gets steeper/shallower than the fixed one. */
export const WALL_HUNT_AIM_TILT_MAX_DEG = 20
type AimGeometry = { wall: number; dir: 1 | -1; x0: number; xLand: number; dy: number; kMin: number; kMax: number; ratio: number }
const aimGeometryCache = new Map<string, AimGeometry>()
function aimGeometry(side: WallSide, bounds: { leftWall: number; rightWall: number }): AimGeometry {
  const cacheKey = `${side}:${bounds.leftWall}:${bounds.rightWall}`
  const cached = aimGeometryCache.get(cacheKey)
  if (cached) return cached
  const ratio = (bounds.rightWall - bounds.leftWall) / (HUNT_WALLS.right - HUNT_WALLS.left)
  const mapX = (x: number) => bounds.leftWall + (x - HUNT_WALLS.left) * ratio
  const wall = side === 'left' ? bounds.leftWall : bounds.rightWall
  const dir = side === 'left' ? 1 : -1
  const kick = rawSample(T.kick, side), land = rawSample(T.land, side)
  const x0 = mapX(kick.placement.x), xLand = mapX(land.placement.x)
  const dy = land.placement.y - kick.placement.y
  // Lower bound: the steepest chord the capped extra lean can still match exactly.
  const chord = Math.atan2(dy, Math.abs(xLand - x0)), cap = WALL_HUNT_AIM_TILT_MAX_DEG * Math.PI / 180
  const kMin = Math.tan(chord) / Math.tan(Math.min(Math.PI / 2 - 1e-6, chord + cap))
  // Upper bound: never press past the far wall harder than the fixed path already does.
  let fit = Infinity
  for (let ms = T.kick; ms <= T.end; ms += 10) {
    const s = rawSample(ms, side)
    const x = mapX(s.placement.x), box = pixelCellBounds(s)
    const farEdge = dir === 1 ? box[2] - s.placement.x : s.placement.x - box[0]
    const anchor = ms <= T.land ? x : xLand
    const room = dir === 1 ? bounds.rightWall - farEdge - (x - anchor) - x0 : x0 + (x - anchor) - farEdge - bounds.leftWall
    const travel = Math.abs(anchor - x0)
    if (travel > 1) fit = Math.min(fit, room / travel)
  }
  const geometry: AimGeometry = { wall, dir, x0, xLand, dy, kMin, kMax: Math.max(1, fit), ratio }
  aimGeometryCache.set(cacheKey, geometry)
  return geometry
}
/** Stretch factor for a recorded player x. fixed (or no record) is exactly 1. */
export function wallHuntAimK(mode: WallHuntAim, playerX: number | undefined, side: WallSide, bounds = arenaBoundsFor('crowned-king-awoken')): number {
  if (!import.meta.env.DEV || mode === 'fixed' || playerX === undefined) return 1
  const g = aimGeometry(side, bounds)
  const d = (playerX - g.wall) * g.dir, d0 = (g.x0 - g.wall) * g.dir
  const aim = Math.max(g.kMin, Math.min(g.kMax, (d - d0) / (WALL_HUNT_AIM_CENTER_PX * g.ratio - d0)))
  return mode === 'aim' ? aim : (1 + aim) / 2
}
export function wallHuntAimLimits(side: WallSide, bounds = arenaBoundsFor('crowned-king-awoken')): { kMin: number; kMax: number } {
  const { kMin, kMax } = aimGeometry(side, bounds)
  return { kMin, kMax }
}
function sampleWallHuntGameBase(ms: number, side: WallSide, bounds = arenaBoundsFor('crowned-king-awoken'), floor = HUNT_FLOOR, startX?: number, aimK = 1) {
  const sample = rawSample(ms, side)
  const { frame, art, placement } = sample
  const ratio = (bounds.rightWall - bounds.leftWall) / (HUNT_WALLS.right - HUNT_WALLS.left)
  // Map the room's coordinates, never stretch the drawing. Contact is corrected below.
  const mapX = (x: number) => bounds.leftWall + (x - HUNT_WALLS.left) * ratio
  placement.x = mapX(placement.x)
  placement.y += floor - HUNT_FLOOR
  if (startX !== undefined && ms < T.wall) {
    const canonicalStart = mapX(rawSample(0, side).placement.x)
    const travel = Math.max(0, Math.min(1, (ms - T.launch) / (T.wall - T.launch)))
    placement.x += (startX - canonicalStart) * (1 - travel)
  }
  if (frame.key?.wall.length) {
    const canonical = huntRealPlacement(frame, side, art, 'fix')
    placement.x = canonical.x + (side === 'left' ? bounds.leftWall - HUNT_WALLS.left : bounds.rightWall - HUNT_WALLS.right)
  }
  if (aimK !== 1 && ms >= T.kick) {
    const g = aimGeometry(side, bounds)
    const anchor = ms <= T.land ? placement.x : g.xLand
    placement.x = g.x0 + (anchor - g.x0) * aimK + (placement.x - anchor)
    if (frame.key && frame.key.ms > T.kick && ms < T.land) {
      // Swoop drawing only: lean nose-down (negative) as the chord steepens, capped.
      const dx = Math.abs(g.xLand - g.x0), cap = WALL_HUNT_AIM_TILT_MAX_DEG * Math.PI / 180
      const extra = Math.atan2(g.dy, dx * aimK) - Math.atan2(g.dy, dx)
      placement.rotation -= Math.max(-cap, Math.min(cap, extra))
    }
  }
  let box = pixelCellBounds(sample)
  placement.x += Math.max(0, bounds.leftWall - box[0]) - Math.max(0, box[2] - bounds.rightWall)
  placement.y += Math.max(0, 8 - box[1]) - Math.max(0, box[3] - floor)
  box = pixelCellBounds(sample)
  return { ...sample, bounds: box }
}
// ---- 壁跳ねの跳びの進み方 (2026-10-19・検収パック 1021 の答え「水中を泳いでいるよう」→ 1022 で「直した版の方が良い」で配布版へ。前の版＝DEV ?huntLeap=now) ----
// 今の版は、襲う絵に替わってから 100〜120ms 横にほぼ動かない (部屋の中ほどからの往は少し後ろへ戻る)。その後だんだん速くなり、着く直前が一番速い。
//   = 「パッと跳んだ絵なのに、その場に少し留まってからだんだん速くなる」(実測 = docs/research/2026-10-19-awoken-executioner-1021-fixes.md)。
// 比較版は、往の跳び (襲う絵の間・770〜1230ms) と帰りの跳び (2440〜2890ms) の横の位置を、跳んだ瞬間が一番速く、着くまでに減速する形へ引き直す。
//   始めと終わりの位置・絵・時刻・高さは今の版のまま。着地の後の「後脚を下ろし停止」の絵には、着いた重みで沈んで戻る動きを足す。
export type WallHuntLeap = 'now' | '1021'
let leapMode: WallHuntLeap = '1021'
export function setWallHuntLeap(raw: string | null | undefined): void {
  leapMode = import.meta.env.DEV && raw === 'now' ? 'now' : '1021'
}
export function wallHuntLeap(): WallHuntLeap { return leapMode }
/** 跳びの進み 0→1。出だしの速さは平均の 1 + k 倍、着く時は 1 - k 倍。 */
export const WALL_HUNT_LEAP_K = 0.6
export function wallHuntLeapEase(p: number): number {
  const t = Math.max(0, Math.min(1, p))
  return t + WALL_HUNT_LEAP_K * t * (1 - t)
}
const LEAP_SEGMENTS = [[T.launch, HUNT_TAKEOFF_KEYS.find(k => k.image === 'takeoff')!.ms], [T.kick, T.land]] as const
/** 着いた重みで沈む量 (縦の縮み)。着いた直後が一番深く、160ms で戻り、残りはゆっくり息をする。 */
export function wallHuntSettleSink(elapsedMs: number): number {
  if (elapsedMs < 0) return 0
  const land = elapsedMs < 160 ? 0.045 * (1 - elapsedMs / 160) ** 2 : 0
  const breath = elapsedMs > 160 ? 0.01 * Math.sin(Math.PI * 2 * (elapsedMs - 160) / 820) ** 2 : 0
  return land + breath
}
export function sampleWallHuntGame(ms: number, side: WallSide, bounds = arenaBoundsFor('crowned-king-awoken'), floor = HUNT_FLOOR, startX?: number, aimK = 1) {
  const sample = sampleWallHuntGameBase(ms, side, bounds, floor, startX, aimK)
  if (leapMode !== '1021') return sample
  const { placement } = sample
  const before = sample.bounds
  const segment = LEAP_SEGMENTS.find(([a, b]) => ms > a && ms < b)
  if (segment) {
    const [a, b] = segment
    const xs = sampleWallHuntGameBase(a, side, bounds, floor, startX, aimK).placement.x
    const xe = sampleWallHuntGameBase(b - 1e-6, side, bounds, floor, startX, aimK).placement.x
    placement.x = xs + (xe - xs) * wallHuntLeapEase((ms - a) / (b - a))
  } else if (sample.frame.key?.image === 'settle') {
    const sink = wallHuntSettleSink(ms - sample.frame.key.ms)
    placement.scaleY *= 1 - sink
    placement.scaleX *= 1 + sink * 0.4
    const after = pixelCellBounds(sample)
    placement.y += before[3] - after[3]
    placement.x += (before[0] + before[2]) / 2 - (after[0] + after[2]) / 2
  } else return sample
  let box = pixelCellBounds(sample)
  placement.x += Math.max(0, bounds.leftWall - box[0]) - Math.max(0, box[2] - bounds.rightWall)
  placement.y += Math.max(0, 8 - box[1]) - Math.max(0, box[3] - floor)
  box = pixelCellBounds(sample)
  return { ...sample, bounds: box }
}
export type WallHuntGameSample = ReturnType<typeof sampleWallHuntGame>
// Mouth centres in source pixels. The three transplanted heads use the donor
// wall-B centre (150, 399) transformed by the recorded head8 affine matrices.
export function wallHuntFacePoint(file: string): readonly [number, number] | undefined {
  const points: Record<string, readonly [number, number]> = {
    'wall_kick_fangs_0912.webp': [150, 399],
    'swoop_bite_head8_0925.webp': [231.663, 421.598],
    'slam_landing_head8_0925.webp': [205.379, 772.386],
    'wall_hunt_kick_head8_0925.webp': [236.146, 625.032],
    'wall_hunt_takeoff_0925.webp': [390, 215],
  }
  return points[file]
}
// ---- DEV 比較版 ?wallDashFix=pounce (src/awoken_wall_dash_fix.ts・2026-10-09) ----
// 往の踏み切りで、主人公との間を最大 100px 詰める跳びかかり。体は「踏み切りの位置 + 跳びかかり」より後ろへ下がらない
//   (今の版は、部屋の中ほどから走ると踏み切りの後 100ms ほど体が 20〜30px 後ろへ戻る。それに負けないよう、走り出す位置 startX を毎 tick 解き直す)。
//   自然な走りが追い越したら、今の版の走りそのものに戻る。跳びかかる距離は踏み切りの tick に1回だけ決める。
/** 往の走りの経過 realMs で使う走り出す位置。from = 溜めの間の startX、px = 跳びかかる距離 (0 なら from のまま)。 */
export function wallHuntPounceStartX(side: WallSide, bounds: { leftWall: number; rightWall: number }, floor: number,
  from: number, px: number, realMs: number): number {
  if (px <= 0) return from
  const dir = side === 'right' ? 1 : -1
  const lab = Math.min(T.wall, T.launch + realMs)
  const travel = (lab - T.launch) / (T.wall - T.launch)
  if (travel >= 1) return from
  const x0 = sampleWallHuntGame(T.launch, side, bounds, floor, from).placement.x
  const natural = sampleWallHuntGame(lab, side, bounds, floor, from).placement.x
  const reach = px * wallDashPounceEase(realMs)
  // 跳びかかった分を今の走りに上乗せし (壁に近づくほど薄れる)、踏み切りの位置 + 跳びかかりより後ろへは下がらない。
  const need = Math.max(reach * (1 - travel), (x0 + dir * reach - natural) * dir)
  return need > 0 ? from + dir * need / (1 - travel) : from
}
/** 踏み切りの間合い (溜めの間の startX から主人公まで) から跳びかかる距離。 */
export function wallHuntPouncePx(side: WallSide, from: number, playerX: number): number {
  const dir = side === 'right' ? 1 : -1
  return Math.max(0, Math.min(WALL_DASH_POUNCE_MAX_PX, (playerX - from) * dir - WALL_DASH_POUNCE_REACH_PX))
}
export function pounceWallHunt(boss: Boss, player: Player): Boss {
  if (wallDashFix() !== 'pounce') return boss
  const hunt = boss.wallHunt
  if (!hunt || !isWallHuntGame(boss) || boss.currentAttack?.id !== 'feralWallDash' || boss.state !== 'active') return boss
  const pounce = hunt.pounce ?? { from: hunt.startX, px: wallHuntPouncePx(hunt.side, hunt.startX, player.x) }
  const startX = wallHuntPounceStartX(hunt.side, arenaBoundsFor(boss.id), boss.y, pounce.from, pounce.px, boss.stateElapsed * 1000)
  return { ...boss, wallHunt: { ...hunt, startX, pounce } }
}

export function wallHuntGameSample(boss: Boss): WallHuntGameSample | undefined {
  if (!isWallHuntGame(boss)) return undefined
  const back = boss.currentAttack!.id === 'feralWallDashBack'
  const side = boss.wallHunt?.side ?? (back ? (boss.facing === 'right' ? 'left' : 'right') : boss.facing)
  const bounds = arenaBoundsFor(boss.id), aim = back ? boss.wallHunt?.aim : undefined
  return sampleWallHuntGame(wallHuntLabTime(boss), side, bounds, boss.y, boss.wallHunt?.startX ?? boss.x,
    aim ? wallHuntAimK(aim.mode, aim.playerX, side, bounds) : 1)
}
/** Records the player x once, on the tick the return telegraph ends (the kick). Never re-aims:
 *  a mode switched mid-leap or a missed kick tick leaves this leap on the fixed path. */
export function recordWallHuntAim(prev: Boss, next: Boss, player: Player): Boss {
  const mode = wallHuntAimMode()
  if (mode === 'fixed' || !isReturnKick(prev, next) || next.wallHunt!.aim) return next
  return { ...next, wallHunt: { ...next.wallHunt!, aim: { mode, playerX: player.x } } }
}
/** The tick the return telegraph (wall hold) ends: the kick-off of the return leap. */
function isReturnKick(prev: Boss, next: Boss): boolean {
  return isWallHuntGame(next) && next.currentAttack!.id === 'feralWallDashBack'
    && prev.currentAttack?.id === 'feralWallDashBack' && prev.state === 'telegraph' && next.state !== 'telegraph'
    && !!next.wallHunt
}

// ---- Wall spirits on the return kick (2026-10-09: OFF in the release build; DEV `?wallHuntSpirits=on` brings them back) ----
// At the kick three normal (parryable) rampage spirits are left at the kicked wall. Each one is the
// rampage's own spirit (spawnTimedSpirit: art, size, homing, hit, parry-return), but held still at the wall
// until launch (no drifting to a seat around the player, no following the player). They touch the
// player these ms after the kick (rampage 0.45 s beat); flight is SPIRIT.flightMs, the hold absorbs the rest.
// Values are provisional. See docs/handoffs/2026-09-24-awoken-wall-hunt-study.md (2026-09-25 wall spirits).
// 10-07 (回避の無敵 100ms の検収・ユーザー「怨念が出てから回避した時に不自然に等距離を保ち続けていた」「2回目の赤が場所によっては回避不可？」):
//   旧 750/1200/1650 は、帰りの跳び (蹴りから 350〜417ms で当たる) を回避すると次の回避が 500ms 出せず (stepCooldownMs)、
//   1個目が回避では抜けられなかった (どの立ち位置でも)。また飛んでいる間は回避の動きにも付いて行き、玉が一緒に滑って見えた。
//   新 = 1個目を「跳びを最も遅く避けても次の回避が間に合う」所 (417+500 の後・≒ 1000) へ、間隔を回避の出し直し 500ms＋50ms に
//   (500 ちょうどだと2・3個目を続けて避ける窓が 1〜3 tick まで細る)、付いて行くのは歩きの速さまで (walkFollowOnly)。
//   弾けるのは変わらない。3個目 (2100) は帰りの戻り (蹴りから 1960ms) の直後で、次の技の当たりより前。旧 = DEV `?wallSpiritTiming=old`。
//   測り方 = tests/awoken_wall_spirit_dodge.test.ts。
export const WALL_HUNT_SPIRIT_ARRIVE_MS = [1000, 1550, 2100] as const
export const WALL_HUNT_SPIRIT_ARRIVE_MS_OLD = [750, 1200, 1650] as const
let spiritTiming: 'new' | 'old' = 'new'
export function setWallHuntSpiritTiming(value: string | null | undefined): void {
  spiritTiming = import.meta.env.DEV && value === 'old' ? 'old' : 'new'
}
export function wallHuntSpiritTiming(): 'new' | 'old' { return import.meta.env.DEV ? spiritTiming : 'new' }
// 2026-10-09 ユーザー「覚醒王の往復突進の2発目の時に怨念が跳ぶやつ、怨念いらないな」＝配布版で外す。
//   絵・飛び方・コードは消さず、既定を off へ。DEV `?wallHuntSpirits=on` で戻して見比べられる (配布版は常に off)。
let spiritsOn = false
export function setWallHuntSpirits(value: string | null | undefined): void {
  spiritsOn = import.meta.env.DEV && value === 'on'
}
export function wallHuntSpiritsOn(): boolean { return import.meta.env.DEV && spiritsOn }
/** Marks the leap once, on the kick tick. A switch flipped mid-leap or a missed kick tick leaves no spirits. */
export function recordWallHuntSpirits(prev: Boss, next: Boss): Boss {
  if (!wallHuntSpiritsOn() || !isReturnKick(prev, next) || next.wallHunt!.spirits) return next
  return { ...next, wallHunt: { ...next.wallHunt!, spirits: true } }
}
/** True only on the tick the mark appears (game.ts spawns then, exactly once per leap). */
export function wallHuntSpiritsDue(before: Boss, after: Boss): boolean {
  return !!after.wallHunt?.spirits && !before.wallHunt?.spirits
}
/** The three spirits, born at the boss still clinging to the wall (the tick before the kick).
 *  They stay exactly where they were born until each one launches (hold), then fly from the wall
 *  straight at the player: same arrival beats, so the farther the player, the faster the flight.
 *  09-25 user: 「怨念は動き出すまでは1箇所でとどまっている方が良い」→「壁際で止まる」. */
export function spawnWallHuntSpirits(atWall: Boss, player: Player): ActiveHit[] {
  if (wallHuntSpiritTiming() === 'old') return WALL_HUNT_SPIRIT_ARRIVE_MS_OLD.map((ms, group) => spawnTimedSpirit(atWall, player, ms, group, false, true))
  return WALL_HUNT_SPIRIT_ARRIVE_MS.map((ms, group) => {
    const hit = spawnTimedSpirit(atWall, player, ms, group, false, true)
    return { ...hit, spirit: { ...hit.spirit!, walkFollowOnly: true } }
  })
}
export function prepareWallHuntGame(boss: Boss): Boss {
  if (!isWallHuntGame(boss)) {
    if (!boss.wallHunt) return boss
    const { wallHunt: _wall, ...rest } = boss
    // 宙で崩れを見て致命へ移った時 (src/stun_cut.ts) も高さを残す (致命の溜めの頭で床へ降りる)。
    return { ...rest, altitudePx: boss.state === 'staggered' || boss.finisherDropFromPx ? boss.altitudePx : 0 }
  }
  // getAttack keeps the legacy definitions in production; normalize each new beat here,
  // including the return beat that inherits the outgoing leap's wall runtime.
  if (boss.wallHunt) return { ...boss, currentAttack: wallHuntGameAttack(boss.currentAttack!) }
  const back = boss.currentAttack!.id === 'feralWallDashBack'
  return { ...boss, currentAttack: wallHuntGameAttack(boss.currentAttack!),
    wallHunt: { side: back ? (boss.facing === 'right' ? 'left' : 'right') : boss.facing, startX: boss.x } }
}
export function placeWallHuntGame(boss: Boss): Boss {
  const sample = wallHuntGameSample(boss)
  if (!sample) return prepareWallHuntGame(boss)
  return { ...boss, x: sample.placement.x, altitudePx: Math.max(0, boss.y - sample.bounds[3]),
    facing: sample.frame.facing === 1 ? 'left' : 'right', behindMs: 0 }
}

// A narrow-phase for the existing boss-following melee hit, not a new hit lifecycle.
// Source alpha runs are cached once on image load. Transparent gaps never deal damage.
type AlphaMask = { width: number; height: number; rows: number[][] }
const masks = new Map<string, AlphaMask>()
const images = new Map<string, HTMLImageElement>()
export function registerWallHuntAlpha(file: string, width: number, height: number, rgba: ArrayLike<number>): void {
  const rows: number[][] = []
  for (let y = 0; y < height; y++) {
    const row: number[] = []
    let start = -1
    for (let x = 0; x <= width; x++) {
      const solid = x < width && rgba[(y * width + x) * 4 + 3] > 16
      if (solid && start < 0) start = x
      if (!solid && start >= 0) { row.push(start, x); start = -1 }
    }
    rows.push(row)
  }
  masks.set(file, { width, height, rows })
}
export const WALL_HUNT_FILES = [...new Set(HUNT_TAKEOFF_KEYS.map(k => rawSample(k.ms, 'left').art.file))]
export function preloadWallHuntArt(): void {
  if (typeof Image === 'undefined') return
  for (const file of WALL_HUNT_FILES) {
    if (images.has(file)) continue
    const img = new Image()
    images.set(file, img)
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d', { willReadFrequently: true })!
      ctx.drawImage(img, 0, 0)
      registerWallHuntAlpha(file, canvas.width, canvas.height, ctx.getImageData(0, 0, canvas.width, canvas.height).data)
    }
    img.src = `/sprites/bosses/crowned-king-awoken/${file}`
  }
}
export function wallHuntImage(file: string): HTMLImageElement | undefined {
  const image = images.get(file)
  return image?.complete && image.naturalWidth > 0 && masks.has(file) ? image : undefined
}
type Point = [number, number]
function clipY(poly: Point[], y: number, below: boolean): Point[] {
  const out: Point[] = []
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length]
    const aIn = below ? a[1] >= y : a[1] <= y
    const bIn = below ? b[1] >= y : b[1] <= y
    if (aIn) out.push(a)
    if (aIn !== bIn) out.push([a[0] + (b[0] - a[0]) * (y - a[1]) / (b[1] - a[1]), y])
  }
  return out
}
export function wallHuntOverlapsRect(sample: WallHuntGameSample, left: number, top: number, right: number, bottom: number): boolean {
  const { placement: at, frame, art, bounds } = sample
  if (right < bounds[0] || left > bounds[2] || bottom < bounds[1] || top > bounds[3]) return false
  const mask = masks.get(art.file)
  if (!mask) return false // unloaded art is also not drawn
  const cos = Math.cos(at.rotation), sin = Math.sin(at.rotation)
  const poly: Point[] = [[left, top], [right, top], [right, bottom], [left, bottom]].map(([x, y]) => {
    const dx = (x - at.x) * frame.facing, dy = y - at.y
    return [at.pivotX + (dx * cos + dy * sin) / at.scaleX, at.pivotY + (-dx * sin + dy * cos) / at.scaleY]
  })
  const y0 = Math.max(0, Math.floor(Math.min(...poly.map(p => p[1]))))
  const y1 = Math.min(mask.height - 1, Math.floor(Math.max(...poly.map(p => p[1]))))
  for (let y = y0; y <= y1; y++) {
    const strip = clipY(clipY(poly, y, true), y + 1, false)
    if (!strip.length) continue
    const lo = Math.min(...strip.map(p => p[0])), hi = Math.max(...strip.map(p => p[0]))
    const row = mask.rows[y]
    for (let i = 0; i < row.length; i += 2) if (hi > row[i] && lo < row[i + 1]) return true
  }
  return false
}
// 当たり枠の上端のこの厚みは、絵の輪郭が触れても数えない (2026-09-26・噛みは口の周りだけの直しと同じ票)。
//   枠の上端 (120) は主人公の髪の頂 (約117) より上。壁跳ね・復で爪先・尻尾・後ろ足の先がここだけを
//   かすって被弾していた (docs/handoffs/2026-09-26-awoken-hit-validity.md NG④)。12 = 108 より下 (髪の塊・額)
//   まで届いた接触だけが当たる。腹・脚が体を通る接触と往路の体当たりは深いので変わらない。
export const WALL_HUNT_GRAZE_PX = 12
export function wallHuntOverlapsPlayer(boss: Boss, player: Player): boolean {
  const sample = wallHuntGameSample(boss)
  return !!sample && wallHuntOverlapsRect(sample, player.x - PLAYER.width / 2,
    player.y - PLAYER_DISPLAY_HEIGHT + WALL_HUNT_GRAZE_PX, player.x + PLAYER.width / 2, player.y)
}
export function wallHuntWorldPoint(sample: WallHuntGameSample, point: readonly [number, number]): [number, number] {
  return huntRealArtPoint(sample.frame, sample.placement, point)
}
export function drawWallHuntHitMask(ctx: CanvasRenderingContext2D, sample: WallHuntGameSample): void {
  const mask = masks.get(sample.art.file)
  if (!mask) return
  const at = sample.placement
  ctx.save()
  ctx.translate(at.x, at.y); ctx.scale(sample.frame.facing, 1); ctx.rotate(at.rotation)
  ctx.scale(at.scaleX, at.scaleY); ctx.translate(-at.pivotX, -at.pivotY)
  mask.rows.forEach((row, y) => {
    for (let i = 0; i < row.length; i += 2) ctx.fillRect(row[i], y, row[i + 1] - row[i], 1)
  })
  ctx.restore()
}
